import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ITeam } from '@core/interfaces/team.interface';
import { TeamService } from '@core/services/team.service';
import { EPermissions } from '@enums/permissions.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { fileExtensionValidator, fileSizeValidator } from '@shared/validators/file-upload.validator';
import { filter } from 'rxjs';
import { IAddTeamDialogData, IAddTeamDialogForm } from './add-team-dialog.interface';

const MAX_TEAM_ICON_SIZE_BYTES = 50 * 1000000;
const ALLOWED_TEAM_ICON_EXTENSIONS = ['gif', 'jpg', 'jpeg', 'png'];

/**
 * "Add team"/"Edit team" dialog - name (required) + an optional media upload (max 50MB,
 * gif/jpg/png), shelled with the same `app-side-dialog` used by Select Role/Select Team/
 * create-campaign-dialog. One component covers both modes, driven by whether `MAT_DIALOG_DATA.team`
 * is present:
 * - absent (including every caller that opens this with no `data` at all, e.g. Select Team's
 *   "Add team" button and the Roles & Teams tab's "Add Team" button) - "create": blank form, Save
 *   calls `TeamService.createTeam`, resolves with the newly created ITeam.
 * - present - "edit": pre-filled from `team` (name + existing icon), Save calls
 *   `TeamService.updateTeam`, resolves with the updated ITeam. Only reachable from the Roles &
 *   Teams tab's "Edit team" row action.
 *
 * Not closable via backdrop/ESC; the X/Cancel controls show a discard-confirmation once the form
 * has changed from its initial (blank, or pre-filled) state, same as Select Role/Select Team.
 */
@Component({
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatErrorComponent,
    DotsSpinnerComponent,
    FileUploaderComponent,
    SideDialogComponent,
  ],
  selector: 'app-add-team-dialog',
  templateUrl: './add-team-dialog.component.html',
})
export class AddTeamDialogComponent {
  public readonly EPermissions = EPermissions;

  private readonly data = inject<IAddTeamDialogData | null>(MAT_DIALOG_DATA, { optional: true });
  private readonly team = this.data?.team ?? null;

  public readonly isEditMode = !!this.team;

  public readonly form = new FormGroup({
    imageFile: new FormControl<ICustomFile | null>(null, [
      fileExtensionValidator(ALLOWED_TEAM_ICON_EXTENSIONS),
      fileSizeValidator(MAX_TEAM_ICON_SIZE_BYTES),
    ]),
    name: new FormControl<string | null>(this.team?.name ?? null, Validators.required),
  });

  public readonly iconId = signal<string | null>(this.team?.iconId ?? null);
  public readonly submitLoading = signal<boolean>(false);

  private readonly fileUploaderRef = viewChild<FileUploaderComponent>('fileUploader');

  /** `FileUploaderComponent` runs its own upload request internally (see `onImageSelected` - by
   * the time it fires, the file is already uploaded and `iconId` is all this dialog needs), so
   * `submitLoading` alone doesn't cover the window between picking a file and the upload actually
   * finishing. Without this, clicking Save mid-upload would submit the team with whatever `iconId`
   * happened to be set at that moment (the previous icon, or none) rather than the new one. */
  public readonly isIconUploading = computed(() => this.fileUploaderRef()?.isUploading() ?? false);

  public readonly hasChanged = computed(
    () =>
      this.form.value.name !== (this.team?.name ?? null) ||
      this.form.value.imageFile !== null ||
      this.iconId() !== (this.team?.iconId ?? null),
  );

  private readonly dialogRef = inject(MatDialogRef<AddTeamDialogComponent, ITeam | undefined>);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly teamService = inject(TeamService);

  public onImageSelected(event: { file: File; id: string }): void {
    this.iconId.set(event.id);
  }

  public onImageRemoved(): void {
    this.iconId.set(null);
  }

  public onSaveClick(): void {
    if (this.form.invalid || this.submitLoading() || this.isIconUploading()) {
      this.form.markAllAsTouched();

      return;
    }

    const formValue = this.form.getRawValue() as IAddTeamDialogForm;
    const payload = { iconId: this.iconId(), name: formValue.name! };

    this.submitLoading.set(true);

    const request$ = this.team
      ? this.teamService.updateTeam(this.team.id, payload)
      : this.teamService.createTeam(payload);

    // No explicit error snackbar here - the global ApiInterceptor already shows one for the
    // failed request (with the actual backend message, where there is one), and a second,
    // generic snackbar here would just immediately replace it with a less useful message
    // (SnackbarService only ever shows one snackbar at a time).
    request$.subscribe({
      error: () => {
        this.submitLoading.set(false);
      },
      next: (team: ITeam) => {
        this.submitLoading.set(false);
        this.dialogRef.close(team);
      },
    });
  }

  public onCloseClick(): void {
    if (this.submitLoading() || this.isIconUploading()) {
      return;
    }

    if (!this.hasChanged()) {
      this.dialogRef.close(undefined);

      return;
    }

    const confirmRef = this.dialog.open<ConfirmDialogComponent, IConfirmDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.cancel'),
        confirmText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.confirm'),
        message: this.translate.instant('pages.settings.userManagement.discardChangesDialog.message'),
        swapButtons: true,
        title: this.translate.instant('pages.settings.userManagement.discardChangesDialog.title'),
      } as IConfirmDialogData,
      width: '400px',
    });

    confirmRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => this.dialogRef.close(undefined));
  }
}
